import { numberFormat, Skeleton } from "@xola/ui-kit";
import { DateFormat, duration, formatDate, now } from "common";
import Highcharts, { SeriesOptionsType, TooltipFormatterContextObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { merge } from "lodash";
import React from "react";
import { useQuery } from "react-query";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { getRevenueReport } from "../../../reports/RevenueReport";
import { useAnalyticsFilterStore } from "../../../stores/reports/analyticsFilterStore";
import { renderToString } from "../../../utils/renderToString";
import { NoTranslate } from "../../Common/NoTranslate";
import { HistogramOptions } from "./Base/HistogramOptions";

export const RevenueChart = () => {
    const seller = useSeller();

    const [from, to] = useAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = useAnalyticsFilterStore((state) => state.filters);
    const productsList: ID[] = filters.products.all ? [] : filters.products.selected ?? [];

    const params = { seller, mode: filters.mode, products: productsList, from, to };
    const { data, isLoading } = useQuery<SeriesOptionsType[]>(
        ["revenueChart", params],
        async () => getRevenueReport(params),
        {
            staleTime: duration(3, "minutes"),
        },
    );

    if (isLoading) {
        return (
            <div className="w-full">
                <Skeleton height={400} />
            </div>
        );
    }

    const chartOptions = getChartOptions(data!, seller.currency);
    return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

const getChartOptions = (series: any, currency: string): Highcharts.Options => {
    // Make the symbol for line chart's legend be a circle instead of a line (as per Figma)
    /* @ts-expect-error */
    Highcharts.seriesTypes.line.prototype.drawLegendSymbol = Highcharts.seriesTypes.area.prototype.drawLegendSymbol;

    const options: Highcharts.Options = {
        series,
        xAxis: {
            className: "no-translate",
        },
        yAxis: {
            className: "no-translate",
            labels: {
                formatter(): string {
                    return numberFormat(this.value, currency, "en-US", 0);
                },
            },
        },
        legend: {
            labelFormatter(): string {
                return this.name.length > 30 ? `${this.name.slice(0, 30)}...` : this.name;
            },
        },
        tooltip: {
            shared: true,
            formatter(): string {
                return renderToString(<TooltipFormatter currency={currency} x={this.x} points={this.points!} />);
            },
        },
    };

    return merge({}, HistogramOptions, options);
};

interface TooltipFormatterProps {
    currency: string;
    x: number;
    points: TooltipFormatterContextObject[];
}

const TooltipFormatter = ({ currency, x, points }: TooltipFormatterProps) => {
    const date = formatDate(now(x), DateFormat.DATE);
    return (
        <div className="bg-black p-1 text-sm text-white">
            <NoTranslate as="div" className="mb-2 font-bold">
                {date}
            </NoTranslate>
            <div className="grid min-w-max grid-cols-3 gap-1">
                {points.map((point) => {
                    const amount = numberFormat(point.y, currency);
                    const name = point.series.name.trim();
                    return (
                        <div key={`${name}-${point.x}-${point.y}`} className="contents text-gray">
                            <div className="col-span-2 truncate">{name}</div>
                            <NoTranslate as="div" className="text-right">
                                {amount}
                            </NoTranslate>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
